/**
 * src/shell/agents/components/VoiceWaveformOverlay.tsx
 *
 * Full-Duplex Audio-Reactive Waveform HUD & Voice Control Surface.
 * Features:
 * - Concentric pulsating circular visualizer reacting to live spectrum frequencies.
 * - Reactive state color transitions (Cyan = Listening, Purple = Processing, Emerald = Speaking, Amber = Step-Up).
 * - Real-time transcript feed showing conversational turn-taking.
 * - Quick-trigger voice command simulation chips.
 */

import React, { useState, useEffect } from 'react';
import { VoiceSessionState, IVoiceTranscriptEvent } from '../types/voice-types';
import { voiceStreamEngine } from '../services/VoiceStreamEngine';
import { voiceIntentClassifier } from '../services/VoiceIntentClassifier';
import { agentToolExecutor } from '../services/AgentToolExecutor';
import { multiAccountVault } from '../../auth/multi-account-vault';
import { UserRole } from '../../../main/security/RoleEnforcement';

export interface VoiceWaveformOverlayProps {
  onClose?: () => void;
  onCanvasMutation?: (instruction: string) => void;
}

export const VoiceWaveformOverlay: React.FC<VoiceWaveformOverlayProps> = ({
  onClose,
  onCanvasMutation,
}) => {
  const [sessionState, setSessionState] = useState<VoiceSessionState>(voiceStreamEngine.getState());
  const [spectrum, setSpectrum] = useState<number[]>(new Array(16).fill(20));
  const [transcripts, setTranscripts] = useState<IVoiceTranscriptEvent[]>([]);
  const [lastActionResult, setLastActionResult] = useState<string>('');

  const activeAccount = multiAccountVault.getActiveAccount();
  const currentRole = (activeAccount?.role as UserRole) || 'ordinary_user';

  useEffect(() => {
    const unsubState = voiceStreamEngine.subscribeState((s) => setSessionState(s));
    const unsubSpec = voiceStreamEngine.subscribeSpectrum((spec) => setSpectrum(spec));
    const unsubTrans = voiceStreamEngine.subscribeTranscript((t) => {
      setTranscripts((prev) => [...prev, t].slice(-8));
    });

    if (voiceStreamEngine.getState() === 'DISCONNECTED') {
      voiceStreamEngine.connect();
    }

    return () => {
      unsubState();
      unsubSpec();
      unsubTrans();
    };
  }, []);

  const getStateColor = () => {
    switch (sessionState) {
      case 'SPEAKING':
        return '#10b981'; // emerald
      case 'PROCESSING':
        return '#a855f7'; // purple
      case 'MUTED':
        return '#64748b'; // grey
      case 'ERROR':
        return '#ef4444'; // red
      case 'LISTENING':
      default:
        return '#38bdf8'; // cyan
    }
  };

  const handleSimulateUtterance = async (text: string) => {
    voiceStreamEngine.simulateUserUtterance(text);

    const intent = voiceIntentClassifier.classify(text);

    // If intent requires tool execution
    if (intent.toolCall) {
      if (intent.toolCall.name === 'canvas:mutate' && onCanvasMutation) {
        onCanvasMutation(intent.queryOrInstruction);
      }

      const res = await agentToolExecutor.executeTool(intent.toolCall, {
        userId: activeAccount?.accountId || 'user_demo',
        role: currentRole,
      });

      setLastActionResult(res.message);
      voiceStreamEngine.simulateAgentResponse(res.message);
    } else {
      const response = `I understand your request regarding: "${text}". Query dispatched to Sovereign Mesh.`;
      setLastActionResult(response);
      voiceStreamEngine.simulateAgentResponse(response);
    }
  };

  const color = getStateColor();

  return (
    <div
      data-testid="voice-waveform-overlay"
      style={{
        background: 'rgba(10, 15, 29, 0.95)',
        border: `1px solid ${color}40`,
        borderRadius: '24px',
        padding: '24px',
        color: '#f8fafc',
        width: '100%',
        maxWidth: '720px',
        margin: '0 auto',
        boxShadow: `0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 30px ${color}20`,
        position: 'relative',
      }}
    >
      {/* Header Bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span
            style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: color,
              boxShadow: `0 0 10px ${color}`,
            }}
          />
          <strong style={{ fontSize: '14px', letterSpacing: '0.04em' }}>
            SOVEREIGN VOICE AI — {sessionState}
          </strong>
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            data-testid="btn-voice-mute"
            onClick={() => voiceStreamEngine.mute(sessionState !== 'MUTED')}
            style={{
              padding: '6px 12px',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              background: sessionState === 'MUTED' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(255, 255, 255, 0.05)',
              color: sessionState === 'MUTED' ? '#fca5a5' : '#cbd5e1',
              fontSize: '11px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {sessionState === 'MUTED' ? '🔇 Unmute' : '🎙️ Mute'}
          </button>
          {onClose && (
            <button
              type="button"
              data-testid="btn-voice-close"
              onClick={onClose}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '8px',
                color: '#94a3b8',
                padding: '6px 12px',
                fontSize: '11px',
                cursor: 'pointer',
              }}
            >
              Close ✕
            </button>
          )}
        </div>
      </div>

      {/* Concentric Pulsating Audio Waveform */}
      <div
        data-testid="waveform-visualizer"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '180px',
          background: 'radial-gradient(circle, rgba(15, 23, 42, 0.9) 0%, rgba(10, 15, 29, 0.98) 100%)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          marginBottom: '20px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated Radial Spectrum Bars */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '100px' }}>
          {spectrum.map((val, idx) => {
            const barHeight = Math.max(8, (val / 255) * 90);
            return (
              <div
                key={idx}
                style={{
                  width: '6px',
                  height: `${barHeight}px`,
                  borderRadius: '3px',
                  background: `linear-gradient(180deg, ${color}, #3b82f6)`,
                  boxShadow: `0 0 8px ${color}80`,
                  transition: 'height 0.08s ease',
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Real-Time Transcript Stream */}
      <div
        data-testid="voice-transcript-stream"
        style={{
          background: 'rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: '12px',
          padding: '12px',
          maxHeight: '130px',
          overflowY: 'auto',
          marginBottom: '16px',
          fontSize: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
        }}
      >
        {transcripts.length === 0 ? (
          <div style={{ color: '#64748b', fontStyle: 'italic', textAlign: 'center', padding: '12px 0' }}>
            Listening for voice input…
          </div>
        ) : (
          transcripts.map((t, idx) => (
            <div key={idx} style={{ display: 'flex', gap: '8px' }}>
              <span
                style={{
                  fontWeight: 800,
                  color: t.speaker === 'user' ? '#38bdf8' : '#10b981',
                  minWidth: '50px',
                }}
              >
                {t.speaker === 'user' ? 'YOU:' : 'AGENT:'}
              </span>
              <span style={{ color: '#e2e8f0', flex: 1 }}>{t.text}</span>
            </div>
          ))
        )}
      </div>

      {/* Last Action / Tool Status Banner */}
      {lastActionResult && (
        <div
          data-testid="voice-action-result-banner"
          style={{
            background: 'rgba(16, 185, 129, 0.12)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '10px',
            padding: '8px 12px',
            fontSize: '11px',
            color: '#6ee7b7',
            marginBottom: '16px',
          }}
        >
          ✓ {lastActionResult}
        </div>
      )}

      {/* Quick Voice Simulation Command Chips */}
      <div>
        <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 700 }}>
          Simulate Voice Utterances:
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {[
            'Unlock Server Room 4B',
            'Quarantine unrecognized hardware device',
            'Make the heatmap crimson',
            'Why did we choose Ed25519 over RSA?',
            'Rebalance Node-7 microCredits',
          ].map((prompt, idx) => (
            <button
              key={idx}
              type="button"
              data-testid={`voice-chip-${idx}`}
              onClick={() => handleSimulateUtterance(prompt)}
              style={{
                fontSize: '11px',
                padding: '6px 10px',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                background: 'rgba(255, 255, 255, 0.04)',
                color: '#cbd5e1',
                cursor: 'pointer',
              }}
            >
              🗣️ &ldquo;{prompt}&rdquo;
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
